/**
 * 66.16 (the owner chose *the flank that takes the point*; BOT_TACTICS.md §3 steps 1 and 6): the route a play's assault takes to
 * its objective — the straight line or a waypoint off it, whichever is least exposed to what the side believes is round the
 * objective (the team picture, live: nothing authored or precomputed), with a little for the extra length and a penalty for
 * corridors other squads already take (Killzone 2's approach corridors). The squad keeps its objective; only its route changes.
 * Pure.
 */
import type { GameState, Vec3 } from '../types';
import { personPointOn, unitPointAt, personLineOfSight } from './fineTerrain';
import { teamPicture } from './picture';
import { LOCAL_FORCE } from './localForce';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { vecAdd, vecDot, vecLength, vecNormalize, vecScale, vecSub } from '../math';
import { dacos, dcos, dsin } from './dmath';

export const FLANK = {
  /** The flank point: this share of the way from the squad to the objective … */
  along: 0.5,
  /** … this far off the line (people), at most this share of the way's length. */
  sidePeople: 25,
  sideShare: 0.6,
  /** The route's candidates: off the line by these shares of the way's length (capped at sidePeople), either side. */
  sideShares: [0.25, 0.45, 0.6],
  /** The beliefs that count: within this (people) of the objective. A route sampled at this many points a leg. */
  watchPeople: 60,
  samples: 6,
  /** The cost of the extra length (per its share of the straight way), and of a corridor within corridorPeople of one taken. */
  lengthCost: 0.5,
  corridorCost: 0.5,
  corridorPeople: 15,
  /** A squad nearer the objective than this (people) goes straight. */
  minPeople: 20,
  /** At the waypoint within this (people). */
  arrivePeople: 5,
  /** The route's and the wait's lengths (s) before the assault goes anyway. */
  routeSeconds: 60,
  waitSeconds: 30,
  /** Commit when (the assault's living + ours round the objective) over their believed number there is at least this, with a
   *  fresh contact; the read's radius (people). */
  commitRatio: 1.5,
  readPeople: 20,
};

/** Is the flank that takes the point on for `team` (`GameConfig.flankTakesPoint`, one value or its side's; off unless true)? */
export function flankOn(state: Pick<GameState, 'config'>, team: number): boolean {
  const v = state.config.flankTakesPoint;
  return Array.isArray(v) ? v[team] === true : v === true;
}

function slerp(a: Vec3, b: Vec3, t: number): Vec3 {
  const ua = vecNormalize(a), ub = vecNormalize(b), d = dacos(Math.max(-1, Math.min(1, vecDot(ua, ub))));
  if (d < 1e-9) return ua;
  return offset(ua, ub, t, 0);
}

const arcPeople = (state: Pick<GameState, 'planets' | 'config'>, planetIndex: number, a: Vec3, b: Vec3) =>
  (dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(a), vecNormalize(b))))) * state.planets[planetIndex].radius) / (PERSON_BODY_HEIGHT * personSetOf(state).k);

/** The point `share` of the way from `a` to `b`, then `side` radians to the left of the way. */
function offset(a: Vec3, b: Vec3, share: number, side: number): Vec3 {
  const ua = vecNormalize(a), ub = vecNormalize(b);
  const along = dacos(Math.max(-1, Math.min(1, vecDot(ua, ub)))) * share;
  const t0 = vecSub(ub, vecScale(ua, vecDot(ub, ua)));
  const dir = vecLength(t0) > 1e-12 ? vecNormalize(t0) : vecNormalize(vecSub({ x: 1, y: 0, z: 0 }, vecScale(ua, ua.x)));
  const p = vecNormalize(vecAdd(vecScale(ua, dcos(along)), vecScale(dir, dsin(along))));
  const left = vecNormalize({ x: p.y * dir.z - p.z * dir.y, y: p.z * dir.x - p.x * dir.z, z: p.x * dir.y - p.y * dir.x });
  return vecNormalize(vecAdd(vecScale(p, dcos(side)), vecScale(left, dsin(side))));
}

/**
 * 66.16, the plan's steps 1 and 6 (BOT_TACTICS.md §3; Killzone 2's approach corridors on our live picture): the assault's route
 * from `from` (its centre) to `objective` on planet `planetIndex` — the straight line or a waypoint off it on either side,
 * whichever costs least. A route's cost is its exposure (each belief of the side round the objective, weighted by its
 * confidence, that has a line to a body on the route at its sample points), plus a little for the extra length, plus a
 * penalty near corridors already taken (`taken`: other squads' waypoints — the play's own, in order, so its squads spread).
 * Every input is the live picture, nothing authored. Null: the straight line is cheapest (go straight, as before).
 */
export function flankWaypoint(state: Pick<GameState, 'planets' | 'config' | 'players' | 'teamTracks' | 'sightings' | 'squads'> & Partial<Pick<GameState, 'smokes'>>, planetIndex: number, from: Vec3, objective: Vec3, team: number, tick: number, taken: readonly Vec3[] = []): Vec3 | null {
  const planet = state.planets[planetIndex];
  if (!planet) return null;
  const dist = arcPeople(state, planetIndex, from, objective);
  if (dist < FLANK.minPeople) return null;
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
  const beliefs = teamPicture(state, team, tick).filter(b => b.planetIndex === planetIndex && b.confidence >= LOCAL_FORCE.minConfidence && arcPeople(state, planetIndex, b.surfaceDir, objective) <= FLANK.watchPeople);
  const eyes = beliefs.map(b => ({ eye: personPointOn(planet, vecNormalize(b.surfaceDir), ps, ps.losEyeHeight), w: b.confidence }));
  const exposure = (a: Vec3, b: Vec3): number => {
    let e = 0;
    for (let k = 1; k <= FLANK.samples; k++) {
      const at = unitPointAt(planet, slerp(a, b, k / (FLANK.samples + 1)), ps, ps.hitboxCenter);
      for (const x of eyes) if (personLineOfSight(x.eye, at, state)) e += x.w;
    }
    return e / FLANK.samples;
  };
  const cost = (via: Vec3 | null): number => {
    const legs = via ? exposure(from, via) + exposure(via, objective) : exposure(from, objective);
    const len = via ? arcPeople(state, planetIndex, from, via) + arcPeople(state, planetIndex, via, objective) : dist;
    const crowd = via ? taken.filter(t => arcPeople(state, planetIndex, t, via) <= FLANK.corridorPeople).length : 0;
    return legs + FLANK.lengthCost * (len - dist) / dist + FLANK.corridorCost * crowd;
  };
  let best: { via: Vec3 | null; c: number } = { via: null, c: cost(null) };
  for (const side of [1, -1]) for (const share of FLANK.sideShares) {
    const sidePeople = Math.min(FLANK.sidePeople, share * dist);
    const via = offset(from, objective, FLANK.along, (side * sidePeople * person) / planet.radius);
    const c = cost(via);
    if (c < best.c - 1e-9) best = { via, c };
  }
  return best.via;
}
